// Which locale numeric dates are formatted in, and which number goes first — the third copy of a
// rule shared with iOS (`TopBarGreetingDate.dateFormattingLocale` / `.localeIsMonthFirst`) and
// Android (`ui/Components.kt`). Keep the three identical.

// The ACCOUNT-REGION OVERRIDE rule: a locale that keeps the device/browser LANGUAGE but swaps in the
// account's REGION. Kept as the third copy of a rule shared with native so the surface that needs it —
// the Android Auto car (`BriefArtwork`), which has NO user locale to read (`Locale.current` → en_US) —
// resolves month-vs-day order the same way everywhere.
//
// ⚠️ The web header (`GreetingDate`) and the two native phone headers deliberately DO NOT apply this —
// they follow the DEVICE/browser locale directly. Applying it to a phone/browser made a US-region
// account render month-first for every device language (a Hebrew phone still showing `08.31`), ignoring
// the user's own chosen format. This override is only right where there is no user locale at all (the
// car). Do not reintroduce it into a surface that has one.
//
// LANGUAGE stays the browser's, always. The account's `languageCode` is the site's content language and
// is resolved separately (`resolveInitialLocale`); borrowing it here would let a formatting concern
// change the words on the page. Only the REGION is taken. `accountCountry` null → the device locale
// unchanged.
export function dateFormattingLocale(device: string | undefined, accountCountry: string | null | undefined): string | undefined {
  // TWO LETTERS, checked — not merely two characters. Android's `Locale.Builder` THROWS on a 2-char
  // non-alpha region, so the same check has to be here or the three surfaces stop agreeing on the one
  // input where they can differ.
  const region = accountCountry?.trim().toUpperCase();
  if (!region || !/^[A-Z]{2}$/.test(region)) return device;
  const language = device?.split('-')[0];
  if (!language) return `und-${region}`;
  return `${language}-${region}`;
}

// Whether `locale` orders the month before the day in an all-numeric date (en-US "M/d" → true;
// en-GB "dd/MM", de-DE "dd.MM." → false). Falls back to day-first when the probe throws or either
// field is absent — the same default both native helpers document.
export function localeIsMonthFirst(locale: string | undefined, now: Date): boolean {
  try {
    const parts = new Intl.DateTimeFormat(locale, { month: 'numeric', day: 'numeric' }).formatToParts(now);
    const m = parts.findIndex((p) => p.type === 'month');
    const d = parts.findIndex((p) => p.type === 'day');
    return m !== -1 && d !== -1 && m < d;
  } catch {
    return false;
  }
}
